import type { TaskDistanceView } from '../../api/task-distances';
import { formatDuration } from '../../utils/date-format';
import {
  AT_LEAST,
  UNKNOWN_DISTANCE,
  boundedMetres,
  wholeMetres,
} from '../../utils/distance-format';

/**
 * How the Tasks tab writes a task's distance and cleaning time, on screen
 * and in the CSV. One place, so the live table, the older view, the
 * phone cards and the export never disagree about a number. The distance
 * itself is written by `utils/distance-format.ts` -- whole metres, always
 * `m`, never kilometres (that module says why).
 */

/** Written in front of a figure that is a LOWER bound. */
export const LOWER_BOUND_MARK = AT_LEAST;

const PARTIAL_TITLE =
  'At least this much. Part of this job could not be measured, so the true figure is higher.';
const RUNNING_TITLE =
  'So far. The robot reports a new total about every 15 seconds while the job runs.';
/** Both at once: a running job's total after a gap in its readings. */
const PARTIAL_RUNNING_TITLE =
  'At least this much so far. Part of this job could not be measured, so the true figure is higher, and the robot reports a new total about every 15 seconds while the job runs.';

/**
 * The lower bound's note where a hover cannot show it: a task drawer,
 * which is read on a phone as often as not. The column's hover note
 * (`PARTIAL_TITLE`), said about the mark.
 */
export const LOWER_BOUND_NOTE = `${AT_LEAST} means at least this much. Part of this job could not be measured, so the true figure is higher.`;

/** Written after a total still growing, where a hover cannot say so. */
export const RUNNING_SUFFIX = 'so far';

export interface DistanceCell {
  text: string;
  title?: string;
  /**
   * What the figure is short of being a plain measurement, for a surface
   * that must say it in words rather than on hover (a task drawer, read
   * on a phone). TWO FLAGS, NOT ONE: a running job's total after a gap in
   * its readings is both a lower bound AND still growing, and a drawer
   * that could say only one of them wrote "≥ 1,235 m" -- which reads
   * exactly like an ended job's final figure. Absent for a plain figure
   * and for a dash.
   */
  lowerBound?: true;
  /** A total still growing: "so far". */
  growing?: true;
}

/**
 * Whether a task's totals are still growing: it is running, or paused
 * mid-run and will carry on. The Tasks table and the task drawer ask the
 * same question, so they cannot disagree about which figures are "so far".
 */
export function distanceStillGrowing(status: string): boolean {
  return status === 'active' || status === 'interrupted';
}

const UNKNOWN: DistanceCell = { text: UNKNOWN_DISTANCE };

/**
 * A measured figure's cell. A lower bound and a total still growing are
 * independent -- either, both or neither -- and the hover says whichever
 * apply. `growing` is the caller's `running` for a total whose finals
 * have not arrived: once they have, or once the task has ended, the
 * figure is simply the number.
 */
function measuredCell(text: string, view: TaskDistanceView, running: boolean): DistanceCell {
  const growing = running && !view.final;
  if (view.partial && growing) {
    return { text, title: PARTIAL_RUNNING_TITLE, lowerBound: true, growing: true };
  }
  if (view.partial) return { text, title: PARTIAL_TITLE, lowerBound: true };
  if (growing) return { text, title: RUNNING_TITLE, growing: true };
  return { text };
}

/**
 * The Distance cell. A dash -- never "0 m" -- when nothing was measured:
 * a job that has not started, one that ended before counting began, or a
 * fleet that reports no odometer. `running` marks a total that is still
 * growing, so it says so on hover.
 */
export function distanceCell(
  view: TaskDistanceView | null | undefined,
  running = false,
): DistanceCell {
  if (!view) return UNKNOWN;
  return measuredCell(boundedMetres(view.distanceM, view.partial), view, running);
}

/**
 * The Cleaning time cell: HEAD-DOWN time (`brush_down_clean_s`), the time
 * the robot actually spent cleaning -- not the vendor's "clean running"
 * flag, which is already on during the undock and the drive to where the
 * coverage starts. A job that is not a clean shows `0s`: it was measured,
 * and it cleaned nothing.
 */
export function cleaningTimeCell(
  view: TaskDistanceView | null | undefined,
  running = false,
): DistanceCell {
  if (!view || view.brushDownCleanS === null) return UNKNOWN;
  const text = formatDuration(view.brushDownCleanS);
  return measuredCell(view.partial ? `${LOWER_BOUND_MARK} ${text}` : text, view, running);
}

/** What Distance sorts by. `null` -- after every measured row, in either
 *  direction -- when nothing was measured: a dash is not a short trip. */
export function distanceSortValue(view: TaskDistanceView | null | undefined): number | null {
  return view ? view.distanceM : null;
}

export function cleaningTimeSortValue(view: TaskDistanceView | null | undefined): number | null {
  return view && view.brushDownCleanS !== null ? view.brushDownCleanS : null;
}

/**
 * The CSV's distance fields, in `distanceCsvValues` order, after every
 * existing column.
 *
 * `active_clean_s` IS THE CLEANING TIME ON SCREEN -- head-down seconds
 * (`brushDownCleanS`), the figure the Cleaning time column and the
 * Metrics card show -- because that is the column a spreadsheet reader
 * takes for "active cleaning time", and the name Akash's spec gave it. It
 * is NOT the server field of the same name: the adapter's `active_clean_s`
 * is the vendor's "clean running" flag, which is already on during the
 * undock and the drive to the coverage start (3-11% of a clean in the
 * 2026-10-19 capture), and a sheet summing it for workload saved would
 * never match the console. That figure is exported too, for comparison,
 * under a name that says what it is: `clean_flag_s`.
 */
export const TASK_CSV_DISTANCE_FIELDS = [
  'distance_m',
  'clean_distance_m',
  'active_clean_s',
  'clean_flag_s',
  'distance_partial',
] as const;

/**
 * The CSV values: plain numbers a spreadsheet can add up -- WHOLE metres,
 * the figure the Distance column shows (the odometer is good to a metre
 * or two, not a decimetre), with no thousands separator; whole seconds
 * -- blanks when nothing was measured, and
 * `distance_partial` as `true`/`false` so a sheet can filter out the
 * lower bounds. See `TASK_CSV_DISTANCE_FIELDS` for which seconds are
 * which; `active_clean_s` is blank exactly when the Cleaning time column
 * shows a dash.
 */
export function distanceCsvValues(view: TaskDistanceView | null | undefined): string[] {
  if (!view) return TASK_CSV_DISTANCE_FIELDS.map(() => '');
  const metres = (m: number) => String(wholeMetres(m));
  const seconds = (s: number) => String(Math.round(s));
  return [
    metres(view.distanceM),
    metres(view.cleanDistanceM),
    view.brushDownCleanS === null ? '' : seconds(view.brushDownCleanS),
    seconds(view.activeCleanS),
    view.partial ? 'true' : 'false',
  ];
}
